/**
 * Custom date picker: syncs with a hidden input (YYYY-MM-DD).
 * Markup root: [data-custom-datepicker]
 */
(function () {
  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function toYMD(d) {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  function parseYMD(s) {
    if (!s || typeof s !== 'string') return null;
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
    if (!m) return null;
    var y = parseInt(m[1], 10);
    var mo = parseInt(m[2], 10) - 1;
    var day = parseInt(m[3], 10);
    var d = new Date(y, mo, day);
    if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== day) return null;
    return d;
  }

  function parseBoundary(el, attr) {
    var v = el.getAttribute(attr);
    if (!v) return null;
    return parseYMD(v);
  }

  function clampViewDate(y, m, minD, maxD) {
    var d = new Date(y, m, 1);
    if (minD) {
      var firstMin = new Date(minD.getFullYear(), minD.getMonth(), 1);
      if (d < firstMin) return { y: minD.getFullYear(), mo: minD.getMonth() };
    }
    if (maxD) {
      var firstMax = new Date(maxD.getFullYear(), maxD.getMonth(), 1);
      if (d > firstMax) return { y: maxD.getFullYear(), mo: maxD.getMonth() };
    }
    return { y: y, mo: m };
  }

  function formatDisplay(ymd) {
    if (!ymd) return '';
    var d = parseYMD(ymd);
    if (!d) return ymd;
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  function CustomDatePicker(root) {
    this.root = root;
    this.hidden = root.querySelector('[data-dp-hidden]');
    this.trigger = root.querySelector('[data-dp-trigger]');
    this.displayEl = root.querySelector('[data-dp-display]');
    this.panel = root.querySelector('[data-dp-panel]');
    this.titleEl = root.querySelector('[data-dp-title]');
    this.titleToggle = root.querySelector('[data-dp-title-toggle]');
    this.titleChevron = root.querySelector('[data-dp-title-chevron]');
    this.gridEl = root.querySelector('[data-dp-grid]');
    this.prevBtn = root.querySelector('[data-dp-prev]');
    this.nextBtn = root.querySelector('[data-dp-next]');
    this.backdrop = root.querySelector('[data-dp-backdrop]');
    this.yearMode = false;

    this.minD = parseBoundary(root, 'data-min');
    if (!this.minD && this.hidden.getAttribute('min')) this.minD = parseYMD(this.hidden.getAttribute('min'));
    this.maxD = parseBoundary(root, 'data-max');
    if (!this.maxD && this.hidden.getAttribute('max')) this.maxD = parseYMD(this.hidden.getAttribute('max'));

    var now = new Date();
    this.todayY = now.getFullYear();
    this.todayM = now.getMonth();
    this.todayD = now.getDate();

    var initial = (this.hidden && this.hidden.value) || '';
    var sel = parseYMD(initial);
    if (sel) {
      this.viewY = sel.getFullYear();
      this.viewM = sel.getMonth();
    } else {
      var clamp = clampViewDate(this.todayY, this.todayM, this.minD, this.maxD);
      this.viewY = clamp.y;
      this.viewM = clamp.mo;
    }

    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onTitleToggle = this._onTitleToggle.bind(this);

    this.trigger.addEventListener('click', this._onTriggerClick);
    if (this.backdrop) this.backdrop.addEventListener('click', this._onBackdropClick);
    if (this.titleToggle) this.titleToggle.addEventListener('click', this._onTitleToggle);
    this.prevBtn.addEventListener('click', this._onPrevClick.bind(this));
    this.nextBtn.addEventListener('click', this._onNextClick.bind(this));

    this._syncDisplay();
    this._renderCalendar();
  }

  CustomDatePicker.prototype._yearInRange = function (y) {
    var start = new Date(y, 0, 1);
    var end = new Date(y, 11, 31);
    if (this.minD) {
      var minAllow = new Date(this.minD.getFullYear(), this.minD.getMonth(), this.minD.getDate());
      if (end < minAllow) return false;
    }
    if (this.maxD) {
      var maxAllow = new Date(this.maxD.getFullYear(), this.maxD.getMonth(), this.maxD.getDate());
      if (start > maxAllow) return false;
    }
    return true;
  };

  CustomDatePicker.prototype._getYearBounds = function () {
    var minY = this.minD ? this.minD.getFullYear() : 1900;
    var maxY = this.maxD ? this.maxD.getFullYear() : this.todayY;
    if (minY > maxY) {
      var t = minY;
      minY = maxY;
      maxY = t;
    }
    return { minY: minY, maxY: maxY };
  };

  CustomDatePicker.prototype._onTitleToggle = function (e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.yearMode) {
      this._exitYearMode();
    } else {
      this._enterYearMode();
    }
  };

  CustomDatePicker.prototype._exitYearMode = function () {
    this.yearMode = false;
    if (this.panel) this.panel.classList.remove('custom-datepicker__panel--year-picker');
    if (this.titleToggle) this.titleToggle.setAttribute('aria-label', 'Choose year');
    if (this.titleChevron) this.titleChevron.textContent = '>';
    this._renderCalendar();
  };

  CustomDatePicker.prototype._enterYearMode = function () {
    this.yearMode = true;
    if (this.panel) this.panel.classList.add('custom-datepicker__panel--year-picker');
    if (this.titleEl) this.titleEl.textContent = 'SELECT YEAR';
    if (this.titleToggle) this.titleToggle.setAttribute('aria-label', 'Back to calendar');
    if (this.titleChevron) this.titleChevron.textContent = '<';
    this._renderYearList();
  };

  CustomDatePicker.prototype._renderYearList = function () {
    var bounds = this._getYearBounds();
    var sel = parseYMD(this.hidden.value);
    var selYear = sel ? sel.getFullYear() : null;
    var html = '';
    for (var y = bounds.maxY; y >= bounds.minY; y--) {
      if (!this._yearInRange(y)) continue;
      var classes = ['custom-datepicker__year-btn'];
      if (selYear === y) classes.push('custom-datepicker__year-btn--selected');
      else if (y === this.viewY) classes.push('custom-datepicker__year-btn--current');
      html +=
        '<button type="button" class="' +
        classes.join(' ') +
        '" data-dp-year="' +
        y +
        '">' +
        y +
        '</button>';
    }
    this.gridEl.innerHTML = html;
    var self = this;
    this.gridEl.querySelectorAll('[data-dp-year]').forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        var yr = parseInt(btn.getAttribute('data-dp-year'), 10);
        self._selectYear(yr);
      });
    });
    requestAnimationFrame(function () {
      var focusY = selYear != null ? selYear : self.viewY;
      var el = self.gridEl.querySelector('[data-dp-year="' + focusY + '"]');
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    });
  };

  CustomDatePicker.prototype._selectYear = function (y) {
    if (!this._yearInRange(y)) return;
    this.viewY = y;
    var c = clampViewDate(this.viewY, this.viewM, this.minD, this.maxD);
    this.viewY = c.y;
    this.viewM = c.mo;
    this.yearMode = false;
    if (this.panel) this.panel.classList.remove('custom-datepicker__panel--year-picker');
    if (this.titleToggle) this.titleToggle.setAttribute('aria-label', 'Choose year');
    if (this.titleChevron) this.titleChevron.textContent = '>';
    this._renderCalendar();
  };

  CustomDatePicker.prototype._isOpen = function () {
    return this.panel && !this.panel.hasAttribute('hidden');
  };

  CustomDatePicker.prototype._open = function () {
    this.yearMode = false;
    if (this.panel) this.panel.classList.remove('custom-datepicker__panel--year-picker');
    if (this.titleToggle) this.titleToggle.setAttribute('aria-label', 'Choose year');
    if (this.titleChevron) this.titleChevron.textContent = '>';
    var sel = this.hidden.value;
    var d = parseYMD(sel);
    if (d) {
      this.viewY = d.getFullYear();
      this.viewM = d.getMonth();
    } else {
      var c = clampViewDate(this.todayY, this.todayM, this.minD, this.maxD);
      this.viewY = c.y;
      this.viewM = c.mo;
    }
    this._renderCalendar();
    this.root.classList.add('is-open');
    if (this.backdrop) this.backdrop.removeAttribute('hidden');
    this.panel.removeAttribute('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    document.addEventListener('keydown', this._onKeydown, true);
  };

  CustomDatePicker.prototype._close = function () {
    if (!this.panel) return;
    this.yearMode = false;
    this.panel.classList.remove('custom-datepicker__panel--year-picker');
    if (this.titleToggle) this.titleToggle.setAttribute('aria-label', 'Choose year');
    if (this.titleChevron) this.titleChevron.textContent = '>';
    this.root.classList.remove('is-open');
    if (this.backdrop) this.backdrop.setAttribute('hidden', '');
    this.panel.setAttribute('hidden', '');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('keydown', this._onKeydown, true);
    this.trigger.focus();
  };

  CustomDatePicker.prototype._onTriggerClick = function (e) {
    e.preventDefault();
    if (this._isOpen()) this._close();
    else this._open();
  };

  CustomDatePicker.prototype._onBackdropClick = function (e) {
    e.preventDefault();
    e.stopPropagation();
    this._close();
  };

  CustomDatePicker.prototype._onDocPointerDown = function (e) {
    if (this.backdrop && e.target === this.backdrop) return;
    if (!this.root.contains(e.target)) this._close();
  };

  CustomDatePicker.prototype._onKeydown = function (e) {
    if (e.key !== 'Escape' || !this._isOpen()) return;
    e.preventDefault();
    if (this.yearMode) {
      this._exitYearMode();
    } else {
      this._close();
    }
  };

  CustomDatePicker.prototype._onPrevClick = function (e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.yearMode) return;
    var nm = this.viewM - 1;
    var ny = this.viewY;
    if (nm < 0) {
      nm = 11;
      ny -= 1;
    }
    var c = clampViewDate(ny, nm, this.minD, this.maxD);
    this.viewY = c.y;
    this.viewM = c.mo;
    this._renderCalendar();
  };

  CustomDatePicker.prototype._onNextClick = function (e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.yearMode) return;
    var nm = this.viewM + 1;
    var ny = this.viewY;
    if (nm > 11) {
      nm = 0;
      ny += 1;
    }
    var c = clampViewDate(ny, nm, this.minD, this.maxD);
    this.viewY = c.y;
    this.viewM = c.mo;
    this._renderCalendar();
  };

  CustomDatePicker.prototype._dayAllowed = function (y, m, day) {
    var d = new Date(y, m, day);
    if (this.minD && d < new Date(this.minD.getFullYear(), this.minD.getMonth(), this.minD.getDate())) return false;
    if (this.maxD && d > new Date(this.maxD.getFullYear(), this.maxD.getMonth(), this.maxD.getDate())) return false;
    return true;
  };

  CustomDatePicker.prototype._selectDay = function (y, m, day) {
    if (!this._dayAllowed(y, m, day)) return;
    var d = new Date(y, m, day);
    var ymd = toYMD(d);
    this.hidden.value = ymd;
    this._syncDisplay();
    this.hidden.dispatchEvent(new Event('input', { bubbles: true }));
    this.hidden.dispatchEvent(new Event('change', { bubbles: true }));
    this._renderCalendar();
    this._close();
  };

  CustomDatePicker.prototype._syncDisplay = function () {
    var ph = this.displayEl.getAttribute('data-placeholder') || '';
    var v = this.hidden.value;
    if (v && parseYMD(v)) {
      this.displayEl.textContent = formatDisplay(v);
      this.displayEl.classList.remove('is-placeholder');
    } else {
      this.displayEl.textContent = ph;
      this.displayEl.classList.add('is-placeholder');
    }
  };

  CustomDatePicker.prototype._renderCalendar = function () {
    if (this.yearMode) return;
    var y = this.viewY;
    var m = this.viewM;
    var monthName = new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    if (this.titleEl) this.titleEl.textContent = monthName.toUpperCase();

    var first = new Date(y, m, 1);
    var startPad = first.getDay();
    var daysInMonth = new Date(y, m + 1, 0).getDate();

    var html = '';
    for (var i = 0; i < startPad; i++) {
      html += '<div class="custom-datepicker__cell custom-datepicker__cell--empty" aria-hidden="true"></div>';
    }
    for (var day = 1; day <= daysInMonth; day++) {
      var ymd = toYMD(new Date(y, m, day));
      var allowed = this._dayAllowed(y, m, day);
      var isToday = y === this.todayY && m === this.todayM && day === this.todayD;
      var isSelected = this.hidden.value === ymd;
      var classes = ['custom-datepicker__cell', 'custom-datepicker__day'];
      if (!allowed) classes.push('custom-datepicker__day--disabled');
      if (isToday) classes.push('custom-datepicker__day--today');
      if (isSelected) classes.push('custom-datepicker__day--selected');
      var attrs = '';
      if (!allowed) attrs += ' aria-disabled="true"';
      else attrs += ' tabindex="-1"';
      html += '<button type="button" class="' + classes.join(' ') + '"' + attrs + ' data-dp-day="' + day + '"' + (allowed ? '' : ' disabled') + '>' + day + '</button>';
    }
    var totalCells = startPad + daysInMonth;
    var tail = (7 - (totalCells % 7)) % 7;
    for (var t = 0; t < tail; t++) {
      html += '<div class="custom-datepicker__cell custom-datepicker__cell--empty" aria-hidden="true"></div>';
    }

    this.gridEl.innerHTML = html;
    var self = this;
    this.gridEl.querySelectorAll('.custom-datepicker__day:not(.custom-datepicker__day--disabled)').forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        var dnum = parseInt(btn.getAttribute('data-dp-day'), 10);
        self._selectDay(self.viewY, self.viewM, dnum);
      });
    });

    if (this.prevBtn) {
      if (this.minD) {
        this.prevBtn.disabled = y === this.minD.getFullYear() && m === this.minD.getMonth();
      } else {
        this.prevBtn.disabled = false;
      }
    }
    if (this.nextBtn) {
      if (this.maxD) {
        this.nextBtn.disabled = y === this.maxD.getFullYear() && m === this.maxD.getMonth();
      } else {
        this.nextBtn.disabled = false;
      }
    }
  };

  function boot() {
    document.querySelectorAll('[data-custom-datepicker]:not([data-dp-init])').forEach(function (root) {
      root.setAttribute('data-dp-init', '');
      try {
        new CustomDatePicker(root);
      } catch (err) {
        console.error('CustomDatePicker', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
